// @ts-strict-ignore

import { Webinar, WebinarCSVStatus, WebinarEvents, WebinarParticipant } from "rainbow-web-sdk/models/webinar.model";
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { WebinarServiceRB } from "rainbow-web-sdk/services/webinars/webinars.service";
import { wait } from "rainbow-web-sdk";

export abstract class AbstractWebinarService {
    public abstract add(webinar: Webinar, participant: WebinarParticipant): Promise<Webinar>;
    public abstract approve(webinar: Webinar, emails: string[]): Promise<Webinar>;
    public abstract reject(webinar: Webinar, emails: string[]): Promise<Webinar>;
    public abstract resendInvitationMail(webinar: Webinar, emails: string[]): Promise<Webinar>;
    public abstract getInfo(webinar: Webinar, email: string): Promise<any>;
    public abstract getCSVTemplateUrl(): Promise<string>;
    public abstract getCSVText(webinar: Webinar): Promise<string>;
    public abstract checkCSV(webinar: Webinar, CSVBuffer: string): Promise<any>;
    public abstract importCSV(webinar: Webinar, CSVBuffer: string): Promise<Webinar>;
    public abstract authenticate(openInviteId: string, joinUuid: string, agreement: boolean, accessCode: string): Promise<any>;
    public abstract joinWebinar(openInviteId: string, joinUuid: string, accessCode: string): Promise<any>;
    public abstract joinWebinarAsRainbowAttendee(webinarId: string): Promise<any>;
}

export class WebinarParticipantsService extends AbstractWebinarService {

    private authService: AuthService = AuthService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();

    public static create(webinarService: WebinarServiceRB): WebinarParticipantsService {
        return new WebinarParticipantsService(webinarService);
    }

    constructor(private webinarService: WebinarServiceRB) {
        super();
    }

    public async add(webinar: Webinar, participant: WebinarParticipant): Promise<Webinar> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/invitations/participant`;
            const headers = this.authService.getPostHeader();
            const body = participant.getData();
            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "addParticipant");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async approve(webinar: Webinar, emails: string[]): Promise<Webinar> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/registrations/approve`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ emails });
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data);
            webinar.sendEvent(WebinarEvents.ON_DATA_UPDATE);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "approveParticipants");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async reject(webinar: Webinar, emails: string[]): Promise<Webinar> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/registrations/reject`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ emails });
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data);
            webinar.sendEvent(WebinarEvents.ON_DATA_UPDATE);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "rejectParticipants");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async resendInvitationMail(webinar: Webinar, emails: string[]): Promise<Webinar> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/registrations/mail-resend`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ emails });
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "resendParticipantInvitationMail");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getInfo(webinar: Webinar, email: string): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/registrations?email=${encodeURIComponent(email)}`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            return (await response.json()).data[0];
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getParticipantInfo");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getCSVTemplateUrl(): Promise<string> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/massprovisioning/v1.0/webinars/participants/template`;
            if (this.webinarService.bubbleService.context === 'webinar') url += '?context=webinar';
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getCSVParticipantTemplateUrl");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getCSVText(webinar: Webinar): Promise<string> {
        try {
            const attendees = await this.webinarService.getAttendeesAnalytics(webinar);
            const indexedAttendees = attendees.reduce((acc: any, attendee: any) => { acc[attendee.email] = attendee; return acc; }, {});
            let content = '\nemail;firstName;lastName;company;jobTitle;country;state;attendance\n';
            webinar.participants.forEach((part: WebinarParticipant) => {
                const firstName = part.firstName && part.firstName.length ? this.unEntity(part.firstName) : '';
                const lastName = part.lastName && part.lastName.length ? this.unEntity(part.lastName) : '';
                const company = part.company && part.company.length ? this.unEntity(part.company) : '';
                const jobTitle = part.jobTitle && part.jobTitle.length ? this.unEntity(part.jobTitle) : '';
                const country = part.country && part.country.length ? part.country : '';
                const state = part.state && part.state.length ? part.state : '';
                content += `${part.email && part.email.length ? part.email : ''};${firstName};${lastName};${company};${jobTitle};${country};${state};${part.email && indexedAttendees[part.email] ? (indexedAttendees[part.email].attendance === true ? 'Yes' : 'No') : 'No'};\n`;
            });
            return content.trim();
        }
        catch { return ''; }
    }

    public async checkCSV(webinar: Webinar, CSVBuffer: string): Promise<WebinarCSVStatus> {
        const webinarCSVStatus = WebinarCSVStatus.create();
        try {
            let url = `${config.restServerUrl}/api/rainbow/massprovisioning/v1.0/webinars/participants/imports/check?webinarId=${webinar.id}`;
            if (this.webinarService.bubbleService.context === 'webinar') url += '&context=webinar';
            const headers = this.authService.getPostHeader('text/csv; charset=utf-8');
            const body = CSVBuffer;
            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinarCSVStatus.okCount = data.okCount;
            webinarCSVStatus.errorCount = data.errorCount;
            if (data.errorReport) {
                webinarCSVStatus.errorReport = Object.keys(data.errorReport).map((key: string) => {
                    const info = data.errorReport[key];
                    const item = info.csvSource.split(';');
                    const report: any = {
                        entry: key.split('entry')[1],
                        email: { value: item[0] || '--', status: 'ok' },
                        firstName: { value: item[1] || '--', status: 'ok' },
                        lastName: { value: item[2] || '--', status: 'ok' },
                        company: { value: item[3] || '--', status: 'ok' },
                        jobTitle: { value: item[4] || '--', status: 'ok' },
                        country: { value: item[5] || '--', status: 'ok' },
                    };
                    info.errors.forEach((error) => {
                        report[error.param].status = 'error';
                        if (!report[error.param].msg) report[error.param].msg = '';
                        report[error.param].msg += `${error.msg}<br/>`;
                    });
                    return report;
                });
            }
            return webinarCSVStatus;
        }
        catch (error) {
            webinarCSVStatus.errorDetails = 'UnknownError';
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "checkCSVParticipants");
            if (error.status === 400) {
                if (errorInfo.error.errorDetails) { webinarCSVStatus.errorDetails = errorInfo.error.errorDetails; }
            }
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            return webinarCSVStatus;
        }
    }

    public async importCSV(webinar: Webinar, CSVBuffer: string): Promise<Webinar> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/massprovisioning/v1.0/webinars/participants/imports?webinarId=${webinar.id}`;
            if (this.webinarService.bubbleService.context === 'webinar') url += '&context=webinar';
            const headers = this.authService.getPostHeader('text/csv; charset=utf-8');
            const body = CSVBuffer;
            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "importCSVParticipants");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            return webinar;
        }
    }

    public async getImportDetails(webinar: Webinar): Promise<Webinar> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/massprovisioning/v1.0/webinars/participants/imports/${webinar.id}/details`;
            if (this.webinarService.bubbleService.context === 'webinar') url += '?context=webinar';
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getImportDetails");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            return webinar;
        }
    }

    public async authenticate(openInviteId: string, joinUuid: string, agreement: boolean = false, accessCode: string = null, userData: any = null): Promise<any> {
        // Get account info and authenticate
        const accountInfo = await this.getAccount(openInviteId, joinUuid, agreement, accessCode, userData);

        // Guest account
        if (accountInfo.password) {

            // Wait for MongoDB synchronization (login api not always used the primary)
            await wait(1000);

            // Try to logon up to 3 trials
            try { await this.guestLogon(accountInfo); }
            catch (error) { error.errorDetailsCode = 'webinarParticipantAuthFailure'; throw error; }
        }
        return accountInfo;
    }

    private async guestLogon(accountInfo: any, trial: number = 1): Promise<void> {
        try {
            this.logger.info(`[WebinarServiceConference] guestLogon (trial ${trial})`);
            await this.authService.logon(accountInfo.login, accountInfo.password, false);
        }
        catch (error) {
            if (trial === 3) {
                this.logger.error(`[WebinarServiceParticipants] guestLogon -- failure after ${trial} trials`);
                throw error;
            }
            await wait(1000);
            await this.guestLogon(accountInfo, ++trial);
        }
    }

    private async getAccount(openInviteId: string, joinUuid: string, agreement: boolean = false, accessCode: string = null, userData: any = null): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/account`;
            const headers = this.authService.getPostHeader();
            let body: any = { openInviteId, joinUuid, accessCode };
            if (agreement === true) { body.agreement = true; } else { body.deny = true; };
            if (userData) {
                if (userData.firstName) body.firstName = userData.firstName;
                if (userData.lastName) body.lastName = userData.lastName;
                if (userData.company) body.company = userData.company;
                if (userData.jobTitle) body.jobTitle = userData.jobTitle;
            }
            body = JSON.stringify(body);

            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            const responseData = (await response.json()).data;
            if (agreement === true) {
                const account = window.atob(responseData.account).split(':');
                this.logger.info(`[WebinarServiceParticipants] getAccount -- success`);
                return { login: account[0], password: account[1], state: responseData.state };
            }
            return { };
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getAccount");
            if (errorInfo.error.errorDetailsCode === 403140) {
                const login = window.atob(errorInfo.error.errorDetailsData.account);
                return { login, password: null };
            }
            throw errorInfo.error;
        }
    }

    public async joinWebinar(openInviteId: string, joinUuid: string, accessCode: string = null): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/join`;
            const headers = this.authService.getPostHeader();
            let body: any = { openInviteId, joinUuid, accessCode };
            body = JSON.stringify(body);
            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            const responseData = await response.json();
            this.logger.info(`[WebinarServiceParticipants] joinWebinar -- success`);
            return responseData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "joinWebinar");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    public async joinWebinarAsRainbowAttendee(webinarId: string): Promise<any> {
        try {
            this.logger.info(`[WebinarServiceParticipants] joinWebinarAsRainbowAttendee -- ${webinarId} `);
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinarId}/join`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "POST", headers });
            if (!response.ok) throw response;
            const webinar = await this.webinarService.getWebinar(webinarId);
            await this.webinarService.getWebinarRooms(webinar, false);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "joinWebinar");
            this.logger.error(`[WebinarServiceParticipants] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async declineWebinarInvitation(openInviteId: string, joinUuid: string): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/account`;
            const headers = this.authService.getPostHeader();
            let body: any = { openInviteId, joinUuid, deny: true };
            body = JSON.stringify(body);

            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            this.logger.info(`[WebinarServiceParticipants] declineWebinarInvitation -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "declineWebinarInvitation");
            throw errorInfo.error;
        }
    }

    private unEntity(str: string): string {
        return str.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
    }
}
